<svelte:options tag={null} />
<script lang="typescript">
  import { 
    onMount,
    get_current_component 
  } from "svelte/internal";
  import type { PlayerLimitsResponse } from './PlayerUserLimits.types';
  import { currencySymbol, isPermissionPresent } from '@everymatrix/retail-helpers';
  import '@everymatrix/em-card';
  import '@everymatrix/em-accordion';
  import '@everymatrix/player-user-limits-active';
  import '@everymatrix/player-user-limits-manage';


  export let src: string;
  export let playerid: string;
  export let redirect: string;
  let authHeaders;
  let currency: string;
  let currencyReqCode;
  let limitsReqCode;
  let permissions;
  permissions = {};

  // API Data
  let userLimits: PlayerLimitsResponse;

  // Card Control
  let edit: boolean = false;
  
  const thisComponent = get_current_component();

  const dispatchWcEvent = (name, detail) => {
    thisComponent.dispatchEvent(
      new CustomEvent(name, {
        detail,
        composed: true, // propagate across the shadow DOM
      })
    );
  };
  const getV2Url = (src: string) => {
    const url = new URL('/v2/player/', src)
    return url.href;
  }
  const processResponse = (res, code, fn) => {
    switch(res) {
      case 200:
        fn();
        code = 200;
        break;
      case 401:
        code = 401;
        break;
      case 400:
        code = 400;
        break;
      case 500:
        code = 500;
        break;
    } 
  }

  const fetchCurrency = async (src: string, headers) => {
    try {
      const res = await fetch(src, { headers: { ...headers } });
      const data = await res.json();

      processResponse(res.status, currencyReqCode, () => {
        currency = currencySymbol(data.currency);
      });

    } catch (err) {
      throw new Error(`Api failed with: ${err}`);
    }
  };

  const fetchUserLimits = async (src: string, headers) => {
    try {
      const res = await fetch(src, { headers: { ...headers } });
      const data = await res.json();

      processResponse(res.status, limitsReqCode, () => {
        userLimits = {...data};
      });

    } catch (err) {
      throw new Error(`Api failed with: ${err}`);
    }
  };

  const handleEdit = () => {
    edit = !edit;
    // Send edit event
    dispatchWcEvent("edit", { edit: edit });
  };

  const handleRedirect = () => {
    dispatchWcEvent("navigate", 'player-limits-page');
  };

  const handleEvents = (e) => {
    e.data?.type === "SetAuthHeaders" && 
      JSON.stringify(authHeaders) !== JSON.stringify(e.data.payload) && 
      (authHeaders = { ...e.data.payload })
    ;
  };

  onMount(() => {
    window.postMessage({ type: "GetAuthHeaders" }, "*");
    window.addEventListener("message", handleEvents);
    return () => {
      window.removeEventListener("message", handleEvents);
    }
  });

  const getPemissions = async () => {
    permissions.canEditLimits = await isPermissionPresent(src, 'upp.dashboard.limits', 'WRITE');
    permissions.canNavLimits = await isPermissionPresent(src, 'upp.nav.limits', 'READ');
  }

  $: src && getPemissions();
  // Initialize Data
  $: src && 
      authHeaders && 
      playerid && 
      fetchUserLimits(`${getV2Url(src)}${playerid}/limits`, { ...authHeaders }) &&
      fetchCurrency(`${src}${playerid}/profile`, { ...authHeaders });
</script>


<em-card>
  <!-- Header -->
  <div slot="header" class="card__header">
    <h3 class="card__title">
      Limits
    </h3>
    
    {#if permissions.canEditLimits}
      <div class="card__actions">
        <button 
          class="card__edit"
          class:active={edit}
          on:click="{handleEdit}"
        >
          {#if edit}
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
            </svg>
          {:else}
            <svg class="icon" fill="#2344b0" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
            </svg>
          {/if}
        </button>
      </div>
    {/if}
  </div>

  <!-- Content -->
  <div slot="content" class="card__body">
    <!-- Active Limits -->
    <player-user-limits-active
      on:edit={(e) => {edit = e.detail.edit}}
      src={src}
      playerid={playerid}
      currency={currency}
      edit={edit}
    />

    {#if redirect === "true" && permissions.canNavLimits}
      <button 
        class="button button__cta"
        on:click={handleRedirect}
      >
        Add Limit
      </button>
    {/if}
  </div>
</em-card>


<style lang="scss">
  *,
  *::before,
  *::after {
    margin: 0;
    padding: 0;
    list-style: none;
    outline: none;
    text-decoration: none;
    box-sizing: border-box;
    font-family: "Helvetica Neue", "Helvetica", sans-serif;
  }

  // Card General
  .card {

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__actions { display: flex; }

    &__body {
      display: flex;
      flex-direction: column;
      flex: 1;
    }
    &__edit {
      display: flex;
      align-items: center;
      padding: 5px;
      background: none;
      border: none;
      margin-left: 10px;
      cursor: pointer;
      transition: all .2s ease-in-out;
      

      svg {
        width: 17px;
        height: 17px;
        transition: all .2s ease-in-out;
      }

      &:hover svg,
      &.active svg {
        fill: red;
      }
    }
  }

  .button {
    display: block;
    width: 150px;
    margin: 20px auto 0 auto;
    min-height: 30px;
    padding: 6px 7px;
    color: #fff;
    background: #307fe2;
    border: 1px solid #307fe2;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 16px;
    &:hover {
      background: #000;
      border: 1px solid #000;
    }
  }
</style>
